import React, { useEffect, useRef, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faExclamationCircle,
  faExclamationTriangle,
  faCheckCircle,
  faXmark,
  faInfoCircle,
} from "@fortawesome/free-solid-svg-icons";

// Hook personalizado para acompanhar o valor anterior
function usePrevious<T>(value: T): T | undefined {
  const ref = useRef<T>();
  useEffect(() => {
    ref.current = value;
  }, [value]);
  return ref.current;
}

export type ToastType = "error" | "warning" | "success" | "info";

interface ToastNotificationProps {
  show: boolean;
  type?: ToastType;
  message: string | null;
  onClose: () => void;
  autoClose?: boolean;
  autoCloseTime?: number;
  dangerouslySetInnerHTML?: boolean;
}

const ToastNotification: React.FC<ToastNotificationProps> = ({
  show,
  type = "error",
  message,
  onClose,
  autoClose = true,
  autoCloseTime = 5000,
  dangerouslySetInnerHTML = false,
}) => {
  const [progressValue, setProgressValue] = useState(100);
  const [isExiting, setIsExiting] = useState(false);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const exitTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Acompanha o valor anterior de 'show'
  const prevShow = usePrevious<boolean>(show);

  const handleClose = () => {
    setIsExiting(true);
    if (exitTimeoutRef.current) clearTimeout(exitTimeoutRef.current);
    exitTimeoutRef.current = setTimeout(() => {
      onClose();
      setIsExiting(false);
    }, 300); // Match animation duration
  };

  useEffect(() => {
    const shouldStart = show && autoClose && !prevShow;

    if (shouldStart) {
      setProgressValue(100);

      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      if (intervalRef.current) clearInterval(intervalRef.current);

      const decrementValue = 100 / (autoCloseTime / 50);
      intervalRef.current = setInterval(() => {
        setProgressValue((prev) => Math.max(prev - decrementValue, 0));
      }, 50);

      timeoutRef.current = setTimeout(() => {
        handleClose();
        if (intervalRef.current) clearInterval(intervalRef.current);
      }, autoCloseTime);
    }

    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      if (intervalRef.current) clearInterval(intervalRef.current);
      if (exitTimeoutRef.current) clearTimeout(exitTimeoutRef.current);
    };
  }, [show]); // ← Remova as dependências extras
  // Adicionado prevShow como dependência

  if (!show) return null;

  // Determine toast styles based on type
  const getToastStyles = () => {
    switch (type) {
      case "error":
        return {
          icon: faExclamationCircle,
          gradient: "linear-gradient(135deg, #ff5f6d 20%, #ff8f70 100%)",
          iconClass: "text-white",
          title: "Erro",
          color: "#ff5f6d",
        };
      case "warning":
        return {
          icon: faExclamationTriangle,
          gradient: "linear-gradient(135deg, #ffc15e 20%, #ffad3b 100%)",
          iconClass: "text-white",
          title: "Aviso",
          color: "#ffc15e",
        };
      case "success":
        return {
          icon: faCheckCircle,
          gradient: "linear-gradient(135deg, #4cd964 20%, #5ae57f 100%)",
          iconClass: "text-white",
          title: "Sucesso",
          color: "#4cd964",
        };
      case "info":
        return {
          icon: faInfoCircle,
          gradient: "linear-gradient(135deg, #36b4e5 20%, #4fc3f7 100%)",
          iconClass: "text-white",
          title: "Informação",
          color: "#36b4e5",
        };
    }
  };

  const styles = getToastStyles();

  return (
    <div
      className="toast-notification-wrapper"
      style={{
        position: "fixed",
        top: "20px",
        right: "20px",
        zIndex: 1050,
        animation: isExiting
          ? "slideOut 0.3s ease-in forwards"
          : "slideIn 0.4s cubic-bezier(0.16, 1, 0.3, 1) forwards",
      }}
    >
      <div className="toast-notification bg-body-emphasis">
        <div
          className="toast-icon-wrapper"
          style={{ background: styles.gradient }}
        >
          <FontAwesomeIcon
            icon={styles.icon}
            className={styles.iconClass}
            size="lg"
          />
        </div>
        <div className="toast-content">
          <div className="toast-header">
            <h6 className="toast-title">{styles.title}</h6>
            <button
              onClick={handleClose}
              className="toast-close-btn"
              aria-label="Fechar"
            >
              <FontAwesomeIcon icon={faXmark} />
            </button>
          </div>
          <div className="toast-message">
            {dangerouslySetInnerHTML ? (
              <div
                className="message-text"
                dangerouslySetInnerHTML={{ __html: message || "" }}
              />
            ) : (
              <div className="message-text">{message}</div>
            )}
          </div>
          {autoClose && (
            <div className="progress-container">
              <div
                className="progress-bar"
                style={{
                  width: `${progressValue}%`,
                  background: styles.gradient,
                }}
              />
            </div>
          )}
        </div>
      </div>
      <style>{`
        @keyframes slideIn {
          from {
            transform: translateX(120%);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }
        
        @keyframes slideOut {
          from {
            transform: translateX(0);
            opacity: 1;
          }
          to {
            transform: translateX(120%);
            opacity: 0;
          }
        }
        
        .toast-notification {
          display: flex;
          max-width: 380px;
          min-width: 320px;
          border-radius: 12px;
          box-shadow: 0 5px 15px rgba(0, 0, 0, 0.08);
          overflow: hidden;
        }
        
        .toast-icon-wrapper {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 56px;
          padding: 1rem 0;
        }
        
        .toast-content {
          flex: 1;
          display: flex;
          flex-direction: column;
          position: relative;
        }
        
        .toast-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 0.875rem 1rem 0.25rem;
        }
        
        .toast-title {
          margin: 0;
          font-weight: 600;
          font-size: 0.95rem;
          letter-spacing: 0.01em;
        }
        
        .toast-close-btn {
          background: transparent;
          border: none;
          padding: 0;
          width: 20px;
          height: 20px;
          font-size: 0.875rem;
          color: #9aa0a6;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: color 0.2s ease;
        }
        
        .toast-close-btn:hover {
          color: #5f6368;
        }
        
        .toast-message {
          padding: 0.25rem 1rem 0.875rem;
        }
        
        .message-text {
          margin: 0;
          line-height: 1.4;
          font-size: 0.875rem;
          font-weight: 400;
        }
        
        .progress-container {
          height: 2px;
          width: 100%;
          overflow: hidden;
          background-color: rgba(0, 0, 0, 0.03);
        }
        
        .progress-bar {
          height: 100%;
          transition: width 50ms linear;
        }
        
        @media (prefers-reduced-motion: reduce) {
          .toast-notification, .toast-close-btn {
            animation: none;
            transition: none;
          }
        }
        
        @media (max-width: 576px) {
          .toast-notification-wrapper {
            left: 50%;
            transform: translateX(-50%);
            width: calc(100% - 32px);
            right: auto;
          }
          
          .toast-notification {
            width: 100%;
            min-width: unset;
          }
          
          @keyframes slideIn {
            from {
              transform: translateY(-20px);
              opacity: 0;
            }
            to {
              transform: translateY(0);
              opacity: 1;
            }
          }
          
          @keyframes slideOut {
            from {
              transform: translateY(0);
              opacity: 1;
            }
            to {
              transform: translateY(-20px);
              opacity: 0;
            }
          }
        }
      `}</style>
    </div>
  );
};

export default ToastNotification;
